import {
  Box,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { useHotel } from "../context/HotelContext";
import { useState } from "react";


const HotelOnboarding = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:850px)");
  const { initializeDraft } = useHotel();
  const [loading, setLoading] = useState(false);

  const handleCreateHotel = async () => {
    try {
      setLoading(true);
      const hotelId = await initializeDraft();
      navigate(`/hotel/add/info1/${hotelId}`);
    } catch (error) {
      console.error("Failed to create hotel draft:", error);
    } finally {
      setLoading(false);
    }
  };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: isMobile ? "100px 20px" : "120px 40px",
  };

  const cardStyle = {
    backgroundColor: "#FFFFFF",
    border: "2px solid #D1D1D1",
    borderRadius: "10px",
    padding: isMobile ? "20px" : "30px",
    width: isMobile ? "100%" : "240px",
    height: isMobile ? "auto" : "160px",
    display: "flex",
    flexDirection: isMobile ? "row" : "column",
    justifyContent: "space-between",
    cursor: "pointer",
    transition: "all 0.3s ease",
    position: "relative",
  };

  const cardHoverStyle = {
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
    }
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Paw Icon */}
        <img
          src="/icons/hotelPaw.png"
          alt="Hotel Paw"
          style={{
            width: isMobile ? "60px" : "80px",
            height: "auto",
            marginBottom: "12px"
          }}
        />

        {/* Main Heading */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            fontSize: isMobile ? "24px" : "32px",
            mb: 3,
            color: "#000",
          }}
        >
          {t('HotelOnboardingWelcome')}
        </Typography>

        {/* Description Text */}
        <Typography
          sx={{
            fontSize: isMobile ? "14px" : "16px",
            color: "#666",
            maxWidth: "600px",
            mb: 1,
            lineHeight: 1.6,
          }}
        >
          {t('HotelOnboardingDesc1')}
        </Typography>

        <Typography
          sx={{
            fontSize: "16px",
            color: "#666",
            maxWidth: "700px",
            mb: 6,
            lineHeight: 1.6,
          }}
        >
          {t('HotelOnboardingDesc2')}
        </Typography>

        {/* Cards Container */}
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? 3 : 4,
            flexDirection: isMobile ? "column" : "row",
            width: "100%",
            maxWidth: "700px",
            justifyContent: "flex-start",
          }}
        >
          {/* Create Hotel Card */}
          <Box
            onClick={handleCreateHotel}
            sx={{
              ...cardStyle,
              ...cardHoverStyle,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "600",
                    fontSize: "18px",
                    color: "#000",
                  }}
                >
                  {t('CreateHotel')}
                </Typography>
                <img
                  src="/icons/hotelAdd.svg"
                  alt="Add Hotel"
                  style={{ width: "24px", height: "24px" }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                width: isMobile ? "32px" : "48px",
                height: isMobile ? "32px" : "48px",
                borderRadius: "50%",
                backgroundColor: "#31BACD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1)",
                }
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" sx={{ color: "#31BACD" }} />
              ) : (
                <ArrowRight color="#fff" size={24} />
              )}
            </Box>
          </Box>

          {/* Manage Room Type Card */}
          <Box
            onClick={() => navigate("/hotel/manage")}
            sx={{
              ...cardStyle,
              ...cardHoverStyle,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "600",
                    fontSize: isMobile ? "16px" : "18px",
                    color: "#000",
                  }}
                >
                  {t('ManageRoomType')}
                </Typography>
                <img
                  src="/icons/hotelManage.svg"
                  alt="Manage Hotel"
                  style={{ width: "24px", height: "24px" }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                width: isMobile ? "32px" : "48px",
                height: isMobile ? "32px" : "48px",
                borderRadius: "50%",
                backgroundColor: "#31BACD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1)",
                }
              }}
            >
              <ArrowRight color="#fff" size={24} />
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelOnboarding;
